import React, { useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Image lightbox with prev/next navigation, keyboard support and click-outside close.
 * Pass `images` (string[]) and `index` (number | null). Set `index` to null to close.
 */
const Lightbox = ({ images = [], index, onClose, onChange }) => {
    const open = index !== null && index !== undefined;
    const count = images.length;

    const goPrev = useCallback(() => {
        onChange((index - 1 + count) % count);
    }, [index, count, onChange]);

    const goNext = useCallback(() => {
        onChange((index + 1) % count);
    }, [index, count, onChange]);

    useEffect(() => {
        if (!open) return;
        const onKey = (e) => {
            if (e.key === "Escape") onClose();
            else if (e.key === "ArrowLeft") goPrev();
            else if (e.key === "ArrowRight") goNext();
        };
        document.addEventListener("keydown", onKey);
        document.body.style.overflow = "hidden";
        return () => {
            document.removeEventListener("keydown", onKey);
            document.body.style.overflow = "";
        };
    }, [open, onClose, goPrev, goNext]);

    // Touch swipe support
    const touchStartX = React.useRef(null);
    const onTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
    const onTouchEnd = (e) => {
        if (touchStartX.current == null) return;
        const dx = e.changedTouches[0].clientX - touchStartX.current;
        const T = 50;
        if (dx > T && count > 1) goPrev();
        else if (dx < -T && count > 1) goNext();
        touchStartX.current = null;
    };

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="fixed inset-0 z-[80] bg-black/95 flex items-center justify-center"
                    data-testid="lightbox-overlay"
                    onClick={onClose}
                    onTouchStart={onTouchStart}
                    onTouchEnd={onTouchEnd}
                >
                    {/* Top bar */}
                    <div className="absolute top-0 left-0 right-0 flex items-center justify-between px-4 sm:px-6 py-4 text-white z-10" onClick={(e) => e.stopPropagation()}>
                        <div className="text-xs uppercase tracking-[0.2em] text-white/85" data-testid="lightbox-counter">
                            {index + 1} / {count}
                        </div>
                        <button
                            onClick={onClose}
                            aria-label="Kapat"
                            className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur flex items-center justify-center transition-colors"
                            data-testid="lightbox-close"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    {/* Prev */}
                    {count > 1 && (
                        <button
                            onClick={(e) => { e.stopPropagation(); goPrev(); }}
                            aria-label="Önceki"
                            className="absolute left-3 sm:left-6 z-10 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur flex items-center justify-center text-white transition-colors"
                            data-testid="lightbox-prev"
                        >
                            <ChevronLeft className="w-6 h-6" />
                        </button>
                    )}

                    {/* Main image */}
                    <motion.img
                        key={index}
                        src={images[index]}
                        alt=""
                        initial={{ opacity: 0, scale: 0.96 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.96 }}
                        transition={{ duration: 0.25 }}
                        className="max-w-[92vw] max-h-[82vh] object-contain select-none"
                        onClick={(e) => e.stopPropagation()}
                        draggable={false}
                        data-testid="lightbox-image"
                    />

                    {/* Next */}
                    {count > 1 && (
                        <button
                            onClick={(e) => { e.stopPropagation(); goNext(); }}
                            aria-label="Sonraki"
                            className="absolute right-3 sm:right-6 z-10 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur flex items-center justify-center text-white transition-colors"
                            data-testid="lightbox-next"
                        >
                            <ChevronRight className="w-6 h-6" />
                        </button>
                    )}

                    {/* Thumbnail strip */}
                    {count > 1 && (
                        <div
                            className="absolute bottom-4 left-0 right-0 flex items-center justify-center gap-2 px-4 overflow-x-auto no-scrollbar"
                            onClick={(e) => e.stopPropagation()}
                        >
                            {images.map((img, i) => (
                                <button
                                    key={i}
                                    onClick={() => onChange(i)}
                                    className={`flex-shrink-0 w-14 h-14 overflow-hidden border-2 transition-all ${
                                        i === index ? "border-gold opacity-100" : "border-transparent opacity-50 hover:opacity-80"
                                    }`}
                                    data-testid={`lightbox-thumb-${i}`}
                                >
                                    <img src={img} alt="" className="w-full h-full object-cover" />
                                </button>
                            ))}
                        </div>
                    )}
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default Lightbox;
